/**
 * agentIdentity.js
 *
 * Single source of truth for "who is the currently logged-in agent?"
 *
 * For a standalone agent:   email = agent's own email,  name = agent's name
 * For a group member login: email = member's own email, name = member's name
 *
 * The JWT already contains everything we need:
 *   payload.email         = the email used to log in (member's own email)
 *   payload.memberEmail   = same as above (older JWT path) — or via campaignMembership
 *   payload.memberName    = member's display name
 *   payload.name          = agent / member name (set at login time)
 */

/**
 * Returns { email, name, isGroupMember, groupLocationName } for the
 * currently logged-in agent from the JWT token.
 */
export function getAgentIdentity(token) {
  const fallback = { email: '', name: '', isGroupMember: false, groupLocationName: '' };
  if (!token) return fallback;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    // Detect group member via campaignMembership (new path) or top-level memberEmail (old path)
    const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';
    const membership = payload.campaignMembership?.[selectedCampaign] || {};
    const isMember = membership.isMember || !!payload.memberEmail;

    const email = membership.memberEmail || payload.memberEmail || payload.email || '';
    const name  = membership.memberName  || payload.memberName  || payload.name  || email.split('@')[0];
    const groupLocationName = membership.groupLocationName || payload.groupLocationName || '';

    return { email, name, isGroupMember: isMember, groupLocationName };
  } catch {
    return fallback;
  }
}

/**
 * Helper to extract the effective email + name from a profile API response.
 * Use this when you have the profile object but need the member-scoped values.
 */
export function getEffectiveAgentInfo(profile) {
  if (!profile) return { email: '', name: '' };
  if (profile._isMemberLogin && profile._loggedInMemberEmail) {
    const member = (profile.groupMembers || []).find(m => m.email === profile._loggedInMemberEmail);
    return {
      email: profile._loggedInMemberEmail,
      name:  member?.name || profile._loggedInMemberEmail,
    };
  }
  return { email: profile.email || '', name: profile.name || '' };
}
